import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Play,
    Pause,
    RotateCcw,
    SkipBack,
    SkipForward,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    Maximize2,
    Library,
    Lock,
} from "lucide-react";
import PalletVisualizer from "../../components/PalletVisualizer/PalletVisualizer";
import Rail from "../../components/primitives/Rail";
import ParamGroup from "../../components/primitives/ParamGroup";
import NumericField from "../../components/primitives/NumericField";
import Stat from "../../components/primitives/Stat";
import StatusPill from "../../components/primitives/StatusPill";
import KeyChip from "../../components/primitives/KeyChip";
import IconButton from "../../components/primitives/IconButton";
import TextButton from "../../components/primitives/TextButton";
import TopStatusStrip from "../../components/primitives/TopStatusStrip";
import { packSingle, ApiError } from "../../services/api";
import QuotaNotice, { QuotaMeter } from "../../components/QuotaNotice/QuotaNotice";
import { useAuth } from "../../services/AuthContext";
import SkuPickerModal from "../ManifestPage/SkuPickerModal";
import "../../styles/viz-shell.css";

const STATUS_META = {
    idle: { label: "Idle", tone: "idle", pulse: false },
    packing: { label: "Packing…", tone: "working", pulse: true },
    ready: { label: "Fitted", tone: "success", pulse: false },
    stale: { label: "Stale", tone: "working", pulse: false },
    error: { label: "Error", tone: "danger", pulse: false },
};

function makeJobId() {
    const n = Math.floor(Math.random() * 9000) + 1000;
    return `PA-${n}`;
}

function formatElapsed(ms) {
    if (ms == null) return "—";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
}

function PalletVisualizerPage() {
    const [containerW, setContainerW] = useState(24);
    const [containerH, setContainerH] = useState(18);
    const [containerD, setContainerD] = useState(24);
    const [boxW, setBoxW] = useState(19.5);
    const [boxH, setBoxH] = useState(11);
    const [boxD, setBoxD] = useState(2);

    const [solution, setSolution] = useState(null);
    const [displayCount, setDisplayCount] = useState(0);
    const [packStatus, setPackStatus] = useState("idle");
    const [errMsg, setErrMsg] = useState(null);
    // Quota snapshot from a 402 — non-null means "show the upgrade prompt".
    const [quotaBlock, setQuotaBlock] = useState(null);
    const [jobId, setJobId] = useState(makeJobId);
    const [elapsed, setElapsed] = useState(null);
    const [packedDims, setPackedDims] = useState(null);
    const [skuPickerOpen, setSkuPickerOpen] = useState(false);
    const [playing, setPlaying] = useState(false);

    const { user, refresh } = useAuth();
    const startRef = useRef(null);
    const controlsRef = useRef(null);
    const resetView = () => controlsRef.current?.reset?.();

    const applySkuToBox = (sku) => {
        setBoxW(sku.width);
        setBoxH(sku.height);
        setBoxD(sku.depth);
    };

    const isStale =
        !!solution &&
        !!packedDims &&
        packStatus === "ready" &&
        (Number(containerW) !== packedDims.cW ||
            Number(containerH) !== packedDims.cH ||
            Number(containerD) !== packedDims.cD ||
            Number(boxW) !== packedDims.bW ||
            Number(boxH) !== packedDims.bH ||
            Number(boxD) !== packedDims.bD);

    const totalBoxes = solution?.box_positions?.length ?? 0;

    const placements = useMemo(
        () =>
            solution?.box_positions?.map((p) => ({
                position: p.position,
                dimensions: p.dimensions,
                rotation_type: p.rotation_type,
            })) ?? [],
        [solution]
    );

    const containerVol = useMemo(
        () => Number(containerW) * Number(containerH) * Number(containerD),
        [containerW, containerH, containerD]
    );
    const boxVol = useMemo(
        () => Number(boxW) * Number(boxH) * Number(boxD),
        [boxW, boxH, boxD]
    );
    const utilization = useMemo(() => {
        if (!solution || !containerVol) return null;
        return ((solution.total_boxes * boxVol) / containerVol) * 100;
    }, [solution, containerVol, boxVol]);

    const containerObj = useMemo(
        () => ({
            width: Number(containerW),
            height: Number(containerH),
            depth: Number(containerD),
        }),
        [containerW, containerH, containerD]
    );

    const handlePack = async () => {
        if (packStatus === "packing") return;
        setPackStatus("packing");
        setSolution(null);
        setDisplayCount(0);
        setErrMsg(null);
        setElapsed(null);
        setJobId(makeJobId());
        startRef.current = performance.now();

        const payload = {
            bin: { width: Number(containerW), height: Number(containerH), depth: Number(containerD) },
            box: { width: Number(boxW), height: Number(boxH), depth: Number(boxD) },
        };

        try {
            const result = await packSingle(payload);
            setSolution(result);
            setDisplayCount(result?.box_positions?.length ?? 0);
            setPackedDims({
                cW: Number(containerW),
                cH: Number(containerH),
                cD: Number(containerD),
                bW: Number(boxW),
                bH: Number(boxH),
                bD: Number(boxD),
            });
            setElapsed(performance.now() - startRef.current);
            setPackStatus("ready");
        } catch (err) {
            // 402 quota_exceeded is a product state, not a failure — the API
            // hands back the full usage snapshot, so show the upsell instead
            // of a red error string.
            if (err instanceof ApiError && err.code === "quota_exceeded") {
                setQuotaBlock(err.details || null);
                setErrMsg(null);
                setElapsed(performance.now() - startRef.current);
                setPackStatus("idle");
                refresh().catch(() => {});
                return;
            }
            const msg =
                err instanceof ApiError
                    ? err.message
                    : err?.message || "Pack request failed";
            setErrMsg(msg);
            setElapsed(performance.now() - startRef.current);
            setPackStatus("error");
        }
        // A successful run consumed one pack — pull the fresh count so the
        // meter and any upgrade prompt stay honest without a page reload.
        refresh().catch(() => {});
    };

    const handleReset = () => {
        setSolution(null);
        setDisplayCount(0);
        setPackStatus("idle");
        setErrMsg(null);
        setQuotaBlock(null);
        setElapsed(null);
        setPackedDims(null);
        setPlaying(false);
    };

    const stepFirst = () => { setPlaying(false); setDisplayCount(0); };
    const stepLast = () => { setPlaying(false); setDisplayCount(totalBoxes); };
    const stepNext = () => {
        setPlaying(false);
        setDisplayCount((c) => Math.min(c + 1, totalBoxes));
    };
    const stepPrev = () => {
        setPlaying(false);
        setDisplayCount((c) => Math.max(c - 1, 0));
    };
    const scrubTo = (n) => { setPlaying(false); setDisplayCount(n); };

    const togglePlay = () => {
        if (!solution) return;
        if (playing) {
            setPlaying(false);
            return;
        }
        if (displayCount >= totalBoxes) setDisplayCount(0);
        setPlaying(true);
    };

    // Auto-advance while playing; speed scales so a full build takes a few
    // seconds regardless of box count.
    useEffect(() => {
        if (!playing || !solution) return undefined;
        const tick = Math.max(30, Math.min(160, 2400 / (totalBoxes || 1)));
        const id = setInterval(() => {
            setDisplayCount((c) => Math.min(c + 1, totalBoxes));
        }, tick);
        return () => clearInterval(id);
    }, [playing, solution, totalBoxes]);

    useEffect(() => {
        if (playing && displayCount >= totalBoxes) setPlaying(false);
    }, [playing, displayCount, totalBoxes]);

    const handlersRef = useRef({});
    handlersRef.current = {
        handlePack,
        handleReset,
        stepNext,
        stepPrev,
        stepFirst,
        stepLast,
        resetView,
        togglePlay,
    };

    useEffect(() => {
        const onKey = (e) => {
            const t = e.target;
            const inField =
                t &&
                (t.tagName === "INPUT" ||
                    t.tagName === "TEXTAREA" ||
                    t.tagName === "SELECT" ||
                    t.isContentEditable);
            const h = handlersRef.current;
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                h.handlePack();
                return;
            }
            if (inField) return;
            if (e.key === "ArrowRight") {
                e.preventDefault();
                h.stepNext();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                h.stepPrev();
            } else if (e.key === "Home") {
                e.preventDefault();
                h.stepFirst();
            } else if (e.key === "End") {
                e.preventDefault();
                h.stepLast();
            } else if (e.key.toLowerCase() === "r" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                h.handleReset();
            } else if (e.key.toLowerCase() === "v" && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                h.resetView();
            } else if (e.key === " " && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                h.togglePlay();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const meta = isStale ? STATUS_META.stale : STATUS_META[packStatus];
    const canPack = packStatus !== "packing";
    // Inputs are frozen from the moment a pack starts until Reset — the
    // on-screen solution always matches the dims in the rail.
    const locked = packStatus === "packing" || !!solution;
    const packLabel = packStatus === "packing"
        ? "Packing"
        : isStale
            ? "Re-pack"
            : "Pack";

    return (
        <div className="viz-page viz-page--pack">
            <TopStatusStrip
                left={
                    <>
                        <span className="uppercase-label">Job</span>
                        <span className="mono viz-job-id">#{jobId}</span>
                    </>
                }
                center={
                    <StatusPill tone={meta.tone} pulse={meta.pulse}>
                        {meta.label}
                    </StatusPill>
                }
                right={
                    <>
                        <span className="uppercase-label">Elapsed</span>
                        <span className="mono-num">{formatElapsed(elapsed)}</span>
                    </>
                }
            />

            <div className="viz-page__body">
                <Rail side="left" label="Parameters">
                    <div className="viz-rail__scroll">
                        {locked && (
                            <div className="viz-lock" role="status">
                                <Lock size={14} aria-hidden="true" />
                                <div className="viz-lock__body">
                                    <span>
                                        Dimensions are locked while a pack is on
                                        screen.
                                    </span>
                                    <TextButton
                                        size="sm"
                                        variant="ghost"
                                        icon={RotateCcw}
                                        onClick={handleReset}
                                        disabled={packStatus === "packing"}
                                    >
                                        Reset to edit
                                    </TextButton>
                                </div>
                            </div>
                        )}
                        <QuotaMeter quota={user?.quota} />
                        <ParamGroup title="Container Dimensions">
                            <NumericField label="Width" value={containerW} onChange={setContainerW} disabled={locked} />
                            <NumericField label="Height" value={containerH} onChange={setContainerH} disabled={locked} />
                            <NumericField label="Depth" value={containerD} onChange={setContainerD} disabled={locked} />
                        </ParamGroup>
                        <ParamGroup title="Box / SKU Dimensions">
                            <TextButton
                                icon={Library}
                                fullWidth
                                variant="ghost"
                                onClick={() => setSkuPickerOpen(true)}
                                disabled={locked}
                            >
                                Pick from catalog
                            </TextButton>
                            <NumericField label="Width" value={boxW} onChange={setBoxW} disabled={locked} />
                            <NumericField label="Height" value={boxH} onChange={setBoxH} disabled={locked} />
                            <NumericField label="Depth" value={boxD} onChange={setBoxD} disabled={locked} />
                        </ParamGroup>
                    </div>
                </Rail>

                <section className="viz-canvas" aria-label="3D viewport">
                    <div className="viz-canvas__actions">
                        {quotaBlock && (
                            <QuotaNotice
                                quota={quotaBlock}
                                onDismiss={() => setQuotaBlock(null)}
                            />
                        )}
                        {errMsg && (
                            <div className="viz-error" role="alert">
                                <AlertTriangle size={14} aria-hidden="true" />
                                <span>{errMsg}</span>
                            </div>
                        )}
                        <div className="viz-canvas__actions-row">
                            <TextButton
                                variant="primary"
                                size="lg"
                                icon={Play}
                                loading={packStatus === "packing"}
                                onClick={handlePack}
                                disabled={!canPack}
                            >
                                {packLabel}
                            </TextButton>
                            <TextButton
                                variant="ghost"
                                size="lg"
                                icon={RotateCcw}
                                onClick={handleReset}
                                disabled={packStatus === "idle" && !solution}
                                aria-label="Reset"
                            >
                                Reset
                            </TextButton>
                        </div>
                        <div className="viz-canvas__actions-hint mono">
                            <KeyChip>Cmd + Enter</KeyChip>
                            <span>Run pack</span>
                        </div>
                    </div>
                    {solution && (
                        <button
                            type="button"
                            className="viz-canvas__reset-view focus-ring"
                            onClick={resetView}
                            aria-label="Reset view"
                            title="Reset view (V)"
                        >
                            <Maximize2 size={14} aria-hidden="true" />
                            <span>Reset view</span>
                        </button>
                    )}
                    {solution ? (
                        <PalletVisualizer
                            container={containerObj}
                            placements={placements}
                            bestOrientation={solution.best_orientation}
                            displayCount={displayCount}
                            controlsRef={controlsRef}
                        />
                    ) : (
                        <div className="viz-empty" role="status">
                            <div className="viz-empty__corners" aria-hidden="true">
                                <span className="viz-empty__corner viz-empty__corner--tl" />
                                <span className="viz-empty__corner viz-empty__corner--tr" />
                                <span className="viz-empty__corner viz-empty__corner--bl" />
                                <span className="viz-empty__corner viz-empty__corner--br" />
                            </div>
                            <div className="viz-empty__inner">
                                <div className="uppercase-label viz-empty__eyebrow">
                                    {packStatus === "error" ? "Run failed" : "Awaiting input"}
                                </div>
                                <h2 className="viz-empty__title">
                                    {packStatus === "error"
                                        ? "Pack request did not return a solution."
                                        : "Define a load to begin."}
                                </h2>
                                <p className="viz-empty__lede">
                                    {packStatus === "error"
                                        ? "Check the parameters in the left rail and re-run."
                                        : "Enter container and SKU dimensions in the left rail, then run Pack."}
                                </p>
                            </div>
                        </div>
                    )}
                </section>

                <Rail side="right" label="Build sequence">
                    <div className="viz-rail__scroll">
                        <div className="viz-section viz-stats-grid">
                            <Stat
                                label="Utilization"
                                value={utilization != null ? utilization.toFixed(1) : "—"}
                                unit={utilization != null ? "%" : undefined}
                                tone="accent"
                                size="lg"
                            />
                            <Stat
                                label="Boxes Fitted"
                                value={solution?.total_boxes ?? "—"}
                                size="lg"
                            />
                        </div>

                        <div className={`viz-section viz-deck${solution ? "" : " viz-deck--empty"}`}>
                            <div className="uppercase-label viz-section__label">
                                Build sequence
                            </div>

                            <div className="viz-deck__readout" aria-live="polite">
                                <span className="viz-deck__current">
                                    {solution ? displayCount : "—"}
                                </span>
                                <span className="viz-deck__sep">/</span>
                                <span className="viz-deck__total">
                                    {solution ? totalBoxes : "—"}
                                </span>
                            </div>
                            <div className="uppercase-label viz-deck__readout-label">
                                Boxes placed
                            </div>

                            <input
                                type="range"
                                className="viz-scrub"
                                min={0}
                                max={totalBoxes || 1}
                                value={displayCount}
                                disabled={!solution}
                                onChange={(e) => scrubTo(Number(e.target.value))}
                                style={{
                                    "--scrub-fill": `${totalBoxes ? (displayCount / totalBoxes) * 100 : 0}%`,
                                }}
                                aria-label="Scrub through placement sequence"
                            />

                            <div className="viz-nav" role="group" aria-label="Step through boxes">
                                <button
                                    type="button"
                                    className="viz-nav__btn focus-ring"
                                    onClick={stepPrev}
                                    disabled={!solution || displayCount === 0}
                                >
                                    <ChevronLeft size={20} aria-hidden="true" />
                                    <span>Prev</span>
                                </button>
                                <button
                                    type="button"
                                    className="viz-nav__btn viz-nav__btn--next focus-ring"
                                    onClick={stepNext}
                                    disabled={!solution || displayCount >= totalBoxes}
                                >
                                    <span>Next box</span>
                                    <ChevronRight size={20} aria-hidden="true" />
                                </button>
                            </div>

                            <div className="viz-deck__aux">
                                <IconButton
                                    icon={SkipBack}
                                    label="Back to empty container"
                                    size="md"
                                    onClick={stepFirst}
                                    disabled={!solution || displayCount === 0}
                                />
                                <TextButton
                                    icon={playing ? Pause : Play}
                                    onClick={togglePlay}
                                    disabled={!solution}
                                >
                                    {playing ? "Pause" : "Play"}
                                </TextButton>
                                <IconButton
                                    icon={SkipForward}
                                    label="Show full solution"
                                    size="md"
                                    onClick={stepLast}
                                    disabled={!solution || displayCount >= totalBoxes}
                                />
                            </div>

                            <div className="viz-deck__hint mono">
                                <KeyChip>←</KeyChip>
                                <KeyChip>→</KeyChip>
                                <span>step</span>
                                <span className="viz-deck__hint-sep">·</span>
                                <KeyChip>Space</KeyChip>
                                <span>play</span>
                            </div>
                        </div>

                        <div className="viz-section viz-section--solver">
                            <div className="uppercase-label viz-section__label">
                                Solver
                            </div>
                            <div className="viz-rows">
                                <div className="viz-row">
                                    <span className="uppercase-label">Best orientation</span>
                                    <span className="mono viz-row__val">
                                        {solution?.best_orientation || "—"}
                                    </span>
                                </div>
                                <div className="viz-row">
                                    <span className="uppercase-label">Container vol</span>
                                    <span className="mono-num viz-row__val">
                                        {containerVol.toFixed(0)}
                                        <span className="viz-row__unit">in³</span>
                                    </span>
                                </div>
                                <div className="viz-row">
                                    <span className="uppercase-label">Box vol</span>
                                    <span className="mono-num viz-row__val">
                                        {boxVol.toFixed(2)}
                                        <span className="viz-row__unit">in³</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Rail>
            </div>

            <SkuPickerModal
                open={skuPickerOpen}
                onClose={() => setSkuPickerOpen(false)}
                onSelect={(sku) => {
                    applySkuToBox(sku);
                    setSkuPickerOpen(false);
                }}
            />
        </div>
    );
}

export default PalletVisualizerPage;
